import { createClient } from '@supabase/supabase-js';

// Environment variables
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Check if the necessary variables are set
if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.warn('Supabase URL or anonymous key is not set in environment variables');
}

// Only create the client if we have the required credentials
export const supabase = SUPABASE_URL && SUPABASE_ANON_KEY 
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY)
  : null;

/**
 * Check if the user is authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  if (!supabase) return false;
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (email: string, password: string) => {
  if (!supabase) {
    return { error: { message: 'Supabase client is not initialized' }, data: {} };
  }
  return await supabase.auth.signInWithPassword({
    email,
    password,
  });
};

/**
 * Sign out
 */
export const signOut = async () => {
  if (!supabase) {
    return { error: { message: 'Supabase client is not initialized' } };
  }
  return await supabase.auth.signOut();
};

/**
 * Get current user with metadata
 */
export const getCurrentUser = async () => {
  if (!supabase) {
    return { data: { user: null }, error: { message: 'Supabase client is not initialized' } };
  }
  return await supabase.auth.getUser();
};

/**
 * Update current user's metadata
 * @param metadata - Object containing metadata fields (e.g., { address: "123 Main St" })
 */
export const updateUserMetadata = async (metadata: Record<string, any>) => {
  if (!supabase) {
    return { error: { message: 'Supabase client is not initialized' }, data: null };
  }

  return await supabase.auth.updateUser({
    data: metadata
  });
};

/**
 * Get current user's metadata
 */
export const getUserMetadata = async () => {
  if (!supabase) {
    return { data: null, error: { message: 'Supabase client is not initialized' } };
  }

  const { data: { user }, error } = await supabase.auth.getUser();

  if (error || !user) {
    return { data: null, error: error || { message: 'User not found' } };
  }

  return { data: user.user_metadata, error: null };
}; 